import axios from 'axios';
import echo from './echo';

/** Avoid multiple replace() calls if several requests fail with 401 at once */
let unauthorizedRedirectScheduled = false;

function redirectToCoreLogin() {
  if (unauthorizedRedirectScheduled) return;
  unauthorizedRedirectScheduled = true;
  try {
    localStorage.removeItem('authToken');
  } catch (e) {
    /* ignore */
  }
  const core = process.env.REACT_APP_CORE_APP_URL;
  const uuid = process.env.REACT_APP_UUID;
  if (core && uuid) {
    window.location.replace(`${core}?uuid=${uuid}`);
  }
}

/**
 * Normalize any failed API outcome to:
 * { Status: 'error', Message, HttpStatus?, Errors?, payload? }
 *
 * Covers: HTTP 401 / 403 / 422 / network errors, and HTTP 200 bodies with Status/status === 'error'
 * (via response interceptor rejection).
 */
export function normalizeApiError(error) {
  if (error && error.isNormalizedRejection) {
    const d = error.data || {};
    const msg = d.Message || d.message || 'Request failed';
    return {
      Status: 'error',
      Message: msg,
      HttpStatus: error.httpStatus ?? 200,
      Errors: d.errors,
      payload: d,
    };
  }

  const res = error.response;
  const status = res?.status;
  const data = res?.data;

  let message = error.message || 'Request failed';
  let errors = undefined;

  if (status === 422 && data && typeof data === 'object') {
    errors = data.errors;
    message = data.message || message || 'Validation failed';
  } else if (status === 401 && data && typeof data === 'object') {
    message = data.message || message || 'Unauthenticated.';
  } else if (status === 403 && data && typeof data === 'object') {
    message = data.message || message || 'Forbidden.';
  } else if (data && typeof data === 'object' && !Array.isArray(data)) {
    if (data.Message) message = data.Message;
    else if (data.message) message = data.message;
  }

  const normalized = {
    Status: 'error',
    Message: message,
    HttpStatus: status,
    Errors: errors,
    payload: data,
  };

  if (status === 401) {
    redirectToCoreLogin();
  }

  return normalized;
}

const http = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 40000,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use(
  function (config) {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (
      process.env.REACT_APP_WEBSOCKETS_ENABLED === 'true' &&
      echo
    ) {
      config.headers['X-Socket-ID'] = echo.socketId();
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

http.interceptors.response.use(
  function (response) {
    if (response.config.responseType === 'blob') {
      return response;
    }
    if (
      typeof Blob !== 'undefined' &&
      response.data instanceof Blob
    ) {
      return response;
    }
    const d = response.data;
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      const st = d.Status ?? d.status;
      if (st === 'error') {
        return Promise.reject({
          isNormalizedRejection: true,
          httpStatus: response.status,
          data: d,
        });
      }
    }
    return response;
  },
  function (error) {
    return Promise.reject(error);
  }
);

http.defaultCatch = (error) => normalizeApiError(error);

export default http;
